import { useCallback, useEffect, useState } from "react";
import { GenericAutocomplete } from "../../../../../../../common/GenericAutocomplete";
import {
  BotObject,
  ChatbotValue,
} from "../../../../../../../services/api/instances/botApi/types";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../../../../../services/redux/hooks/use-dispatch";
import { getBotChatsAction } from "../../../../../redux/actions";
import { findSecureBot } from "../../../../../helpers";
import { getUpdatedChatbotValue } from "../../../../../helpers/getUpdatedChatbotValue";
import { TValueForService } from "../../../../../../../services/api/instances/storageApi/types/service_types";
import { AppLabel } from "../../../../../../../shared/components/AppLabel";
import { NormalizedStorageObject } from "../../../../../../../services/api/instances/storageApi/types/object_types";
import styles from "./styles.module.css";

interface Props {
  bots: NormalizedStorageObject<ChatbotValue>[];
  bot: NormalizedStorageObject<ChatbotValue>;
  saveChatbotValue: (
    value: TValueForService<"ai_agents_telegram_bots">,
  ) => void;
}

export const AdministrationBotSelect = (props: Props) => {
  const dispatch = useAppDispatch();
  const [selectedValue, setSelectedValue] = useState<BotObject | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const activeBot = findSecureBot(
      props.bots,
      props.bot.value?.tg_bot_object?.secure_bot_name ?? "",
    );
    setSelectedValue(activeBot);
  }, [props.bot?.id]);

  const onChangeChat = useCallback(
    (newValue: BotObject | null) => {
      setSelectedValue(newValue);
      if (newValue && ((newValue as BotObject)?.ref as string)) {
        dispatch(getBotChatsAction(newValue.ref));
      }
      const updatedValue = getUpdatedChatbotValue(props?.bot?.value, {
        secure_bot_name: newValue ? newValue.bot_name : null,
      });
      props.saveChatbotValue(updatedValue);
    },
    [
      selectedValue,
      props.bot?.value?.tg_bot_object?.secure_bot_name,
      props.bot?.value?.tg_bot_object?.white_list,
      props.bot?.value?.tg_bot_object?.black_list,
      props.bot?.id,
    ],
  );

  return (
    <div className={styles["administration-block"]}>
      <AppLabel>{t("chatbot.bot_administrator_label")}</AppLabel>
      <GenericAutocomplete<BotObject>
        options={props?.bots
          .filter((bot) => bot.value !== null)
          .map((bot) => bot?.value.tg_bot_object)}
        value={selectedValue}
        onChange={onChangeChat}
        getLabel={(bot) => bot.bot_name}
        getValue={(bot) => bot.ref}
        getOptionKey={(bot) => bot.bot_api}
        emptyOptionLabel={t("chatbot.without_bot")}
        autocompleteStyle={"select"}
        isDisabled={!props.bot?.value?.tg_bot_object?.security_check}
        className={styles["autocomplete-margin"]}
      />
    </div>
  );
};
